import React, { useCallback, useMemo } from 'react';
import {
  AccountManagementActionTypesResponseObject,
  AdminProfileResponse,
} from 'web-library/models/AccountManagementConfiguration';
import {
  MutationStatus,
  QueryObserverResult,
  RefetchOptions,
  UseMutateFunction,
} from 'react-query';
import { UseFormReturn } from 'react-hook-form';
import { UploadedFile } from 'web-library/models/UploadedFile';
import { filterAccountManagementActionTypes, prepareFiles } from 'web-library';
import {
  RequestFileRequest,
  RequestFileResponse,
} from 'web-library/models/UserGroupAccessConfiguration/UserGroupAccessConfiguration';
import { AxiosError } from 'axios';
import { useHistory } from 'react-router-dom';
import {
  AdminProfileResponseIsDisableInputs,
  CreateEditAdminSchemaData,
} from './CreateEditAdminSchema';
import RouteKey from '../../../routes/key';
import { IExamContentResponse, IExamMasterCodeItem } from '../../../api/user/model';
import { useGetAccountManagementActionTypes } from '../../../api-refactored/account-management/useGetAccountManagementActionTypes';
import { GenericErrorResponse } from '../../../api';

const { admin } = RouteKey.child;
const { adminAccountManagement } = RouteKey.admin.child;

interface useCreateEditViewAdminAccountDetailsProps {
  formHook: UseFormReturn<CreateEditAdminSchemaData>;
  adminProfileDetails: AdminProfileResponse;
  setDeleteAccountModalMessage: React.Dispatch<React.SetStateAction<string>>;
  isApproverNeededAction: boolean;
  isEditState: boolean;
  setIsEditState: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenConfirmChangesModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShouldSubmit: React.Dispatch<React.SetStateAction<boolean>>;
  files: UploadedFile[];
  setFiles: React.Dispatch<React.SetStateAction<UploadedFile[]>>;
  setApiButtonStatus: React.Dispatch<React.SetStateAction<MutationStatus>>;
  setUploadFileErrorMessage: React.Dispatch<React.SetStateAction<string>>;
  formDefaultValues: CreateEditAdminSchemaData;
  uploadFileErrorMessage: string;
  postRequestFilePresigned: UseMutateFunction<
    RequestFileResponse[],
    AxiosError<any>,
    RequestFileRequest,
    unknown
  >;
  uuid: string;
  setFilteredActionTypes: React.Dispatch<
    React.SetStateAction<AccountManagementActionTypesResponseObject[]>
  >;
}

interface useCreateEditViewAdminAccountDetailsReturn {
  handleConfirmChangesButton: () => void;
  handleEditParticulars: () => void;
  handleConfirmChanges: () => void;
  handleBackToAdminListing: () => void;
  handleBackToAdminListingWithAlert: () => void;
  refetchAccountManagementActionTypes: (
    options?: RefetchOptions | undefined
  ) => Promise<
    QueryObserverResult<
      AccountManagementActionTypesResponseObject[],
      AxiosError<GenericErrorResponse>
    >
  >;
}

export const useCreateEditViewAdminAccountDetails = ({
  formHook,
  adminProfileDetails,
  setDeleteAccountModalMessage,
  isApproverNeededAction,
  isEditState,
  setIsEditState,
  setOpenConfirmChangesModal,
  setShouldSubmit,
  files,
  setFiles,
  setApiButtonStatus,
  setUploadFileErrorMessage,
  formDefaultValues,
  uploadFileErrorMessage,
  postRequestFilePresigned,
  uuid,
  setFilteredActionTypes,
}: useCreateEditViewAdminAccountDetailsProps): useCreateEditViewAdminAccountDetailsReturn => {
  const history = useHistory();
  const { getValues, reset } = formHook;

  const { refetch: refetchAccountManagementActionTypes } = useGetAccountManagementActionTypes({
    onSuccess: (res) =>
      filterAccountManagementActionTypes(
        res,
        adminProfileDetails?.adminModel.activeStatus as string,
        adminProfileDetails?.adminModel.blockStatus as string,
        adminProfileDetails?.adminModel.lockStatus as string,
        adminProfileDetails?.adminModel.credentialType as string,
        setFilteredActionTypes
      ),
  });

  const handleConfirmChangesButton = useCallback(() => {
    if (
      getValues('action')?.code === 'DELETE' &&
      !!adminProfileDetails?.adminModel?.existingRegistrationsMessage
    ) {
      setDeleteAccountModalMessage(adminProfileDetails?.adminModel?.existingRegistrationsMessage);
    } else if (isApproverNeededAction || isEditState) {
      setOpenConfirmChangesModal(true);
    } else {
      setShouldSubmit(true);
      setApiButtonStatus('loading');
    }
  }, [
    adminProfileDetails?.adminModel?.existingRegistrationsMessage,
    getValues,
    isApproverNeededAction,
    isEditState,
    setApiButtonStatus,
    setDeleteAccountModalMessage,
    setOpenConfirmChangesModal,
    setShouldSubmit,
  ]);
  const handleEditParticulars = useCallback(() => {
    reset(formDefaultValues);
    setIsEditState(true);
    setFiles([]);
    setApiButtonStatus('idle');
    setUploadFileErrorMessage('');
  }, [
    formDefaultValues,
    reset,
    setApiButtonStatus,
    setFiles,
    setIsEditState,
    setUploadFileErrorMessage,
  ]);
  const handleConfirmChanges = useCallback(() => {
    if (uploadFileErrorMessage.length === 0) {
      const { filenames } = prepareFiles(files, setFiles);
      setShouldSubmit(true);
      setApiButtonStatus('loading');
      setOpenConfirmChangesModal(false);

      if (filenames.length) {
        postRequestFilePresigned({
          filenames,
          uuid,
        });
      }
    }
  }, [
    files,
    postRequestFilePresigned,
    setApiButtonStatus,
    setFiles,
    setOpenConfirmChangesModal,
    setShouldSubmit,
    uploadFileErrorMessage.length,
    uuid,
  ]);
  const handleBackToAdminListing = useCallback(
    () => history.push(`/${admin}/${adminAccountManagement.route}`),
    [history]
  );

  const handleBackToAdminListingWithAlert = useCallback(
    () =>
      history.push(`/${admin}/${adminAccountManagement.route}`, {
        action: getValues('action')?.description,
        email: adminProfileDetails?.adminModel?.email,
      }),
    [adminProfileDetails?.adminModel?.email, getValues, history]
  );
  return {
    handleConfirmChangesButton,
    handleEditParticulars,
    handleConfirmChanges,
    handleBackToAdminListing,
    handleBackToAdminListingWithAlert,
    refetchAccountManagementActionTypes,
  };
};

interface useEditViewAdminFormValuesProps {
  iExamMasterCode: IExamContentResponse;
  adminProfileDetails: AdminProfileResponse;
  isEditState: boolean;
  isApproverNeededAction: boolean;
  isEdit: boolean;
}

interface useEditViewAdminFormValuesPropsReturn {
  disabledFields: AdminProfileResponseIsDisableInputs;
  formDefaultValues: CreateEditAdminSchemaData;
}

export const useEditViewAdminFormValues = ({
  iExamMasterCode,
  adminProfileDetails,
  isEditState,
  isApproverNeededAction,
  isEdit,
}: useEditViewAdminFormValuesProps): useEditViewAdminFormValuesPropsReturn => {
  const mobileNumberCountryCodeDefaultObject = useMemo(
    () =>
      iExamMasterCode &&
      iExamMasterCode.countryCode.find(
        (obj: IExamMasterCodeItem) =>
          obj.codeValue === adminProfileDetails?.adminModel?.mobileNumberCountryCode?.toString()
      ),
    [adminProfileDetails?.adminModel?.mobileNumberCountryCode, iExamMasterCode]
  );
  const formDefaultValues = useMemo(
    () =>
      ({
        name: adminProfileDetails?.adminModel?.name,
        dateOfBirth: adminProfileDetails?.adminModel?.dateOfBirth,
        accountType: adminProfileDetails?.adminModel?.credentialType,
        nricFinPassport:
            adminProfileDetails?.adminModel?.adminId === '-'
                ? ''
                : adminProfileDetails?.adminModel?.adminId,
        email: adminProfileDetails?.adminModel?.email,
        mobileCountryCode: mobileNumberCountryCodeDefaultObject,
        mobileContactNumber: adminProfileDetails?.adminModel?.mobileNumber,
        remarks: '',
      } as CreateEditAdminSchemaData),
    [
      adminProfileDetails?.adminModel?.credentialType,
      adminProfileDetails?.adminModel?.adminId,
      adminProfileDetails?.adminModel?.dateOfBirth,
      adminProfileDetails?.adminModel?.email,
      adminProfileDetails?.adminModel?.mobileNumber,
      adminProfileDetails?.adminModel?.name,
      mobileNumberCountryCodeDefaultObject,
    ]
  );
  const disabledFields = useMemo(
    () => ({
      action: isEditState,
      approver: !isApproverNeededAction && !isEditState,
      remarks: !isApproverNeededAction && !isEditState,
      name: !isEditState,
      dateOfBirth: !isEditState,
      nricFinPassport: false,
      email: !isEditState,
      mobileCountryCode: !isEditState,
      mobileContactNumber: !isEditState,
      accountType: !isEditState,
      userGroup: !isEditState,
    }),
    [isApproverNeededAction, isEditState]
  );
  return {
    disabledFields,
    formDefaultValues,
  };
};
